import React, {useEffect, useRef, useState} from "react";
import {getResource, updateResource} from "../../../../data/actions/resource";
import LocalStorage from "../../../../util/localStorage";
import Resources from "../../../../data/services/resources";
import {checkPerm, classNames, getProp} from "../../../../common/util/util-helpers";
import FieldsForm from "../../../../common/components/fields/fields-form";
import {Field} from "../../../../data/services/fields";
import {fillFieldsFromData} from "../../../../common/util/util-fields";
import {useDispatch, useSelector} from "react-redux";
import Card from "../../../../common/components/card";
import PageFooter from "../../../../common/components/layout/layout-components/page/page-footer";
import {MAJOR_ACCOUNT_TYPE_EXPENSE, MAJOR_ACCOUNT_TYPE_INCOME} from "../../../../common/util/util-consts";
import {UPDATE_PERM} from "../../../../util/util-constants";
import Button from "../../../../common/components/button";
import {InformationCircleIcon, PlusIcon, TrashIcon} from "@heroicons/react/24/outline";
import Tooltip from "../../../../common/components/tooltip";
import ModalConfirm from "../../../../common/components/modal/modal-confirm";

const RULE_OPTIONS = [
    {value: 0, label: 'text.rule_never'},
    {value: 1, label: 'text.rule_always'},
    {value: 2, label: 'text.rule_ask_on_entry'},
];

const RULE_COLOR_CLASS = {
    0: 'bg-tm-gray-100 text-tm-gray-700',
    1: 'bg-green-100 text-green-800',
    2: 'bg-amber-100 text-amber-800',
};

const RuleSelect = ({value, onChange, translate}) => (
    <select
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className={classNames(
            'text-xs font-medium px-2 py-1 rounded-btn border border-tm-gray-200 cursor-pointer focus:outline-none w-full',
            RULE_COLOR_CLASS[value] ?? RULE_COLOR_CLASS[0]
        )}
    >
        {RULE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
                {translate(opt.label)}
            </option>
        ))}
    </select>
);

const getItemFields = (item = {}) => {
    const hasOut = !!(item.glOutAccountID && item.glOutAccountID !== '');
    const hasIn  = !!(item.glInAccountID  && item.glInAccountID  !== '');
    return {
        name:           new Field('name',           '', [], false,  'text',          {addContainerClass: 'col-span-2', hideLabel: true, labelType: "float"}),
        glOutAccountID: new Field('glOutAccountID', '', [], hasIn,  'select-search', {addContainerClass: 'col-span-3', hideLabel: true, labelType: "float"}, {isClearable: true}),
        glInAccountID:  new Field('glInAccountID',  '', [], hasOut, 'select-search', {addContainerClass: 'col-span-3', hideLabel: true, labelType: "float"}, {isClearable: true}),
    };
};

const ClaimsActionItemsTab = ({translate, setIsTabDirty}) => {
    const dispatch = useDispatch();
    const resource = useSelector((state) => state.resource);

    const isLoading = getProp(resource, 'isLoading', false);
    const uidRef = useRef(0);

    const makeItem = (data = {}) => ({
        _uid: ++uidRef.current,
        name:               data.Name               ?? data.name               ?? '',
        glOutAccountID:     data.GLOutAccountID      ?? data.glOutAccountID      ?? null,
        glOutAccount:       data.GLOutAccount        ?? data.glOutAccount        ?? '',
        glInAccountID:      data.GLInAccountID       ?? data.glInAccountID       ?? null,
        glInAccount:        data.GLInAccount         ?? data.glInAccount         ?? '',
        recoverableRule:      data.RecoverableRule      ?? data.recoverableRule      ?? 0,
        deductibleRule:       data.DeductibleRule       ?? data.deductibleRule       ?? 0,
        lossRunRule:          data.LossRunRule          ?? data.lossRunRule          ?? 0,
        uninsuredExpenseRule: data.UninsuredExpenseRule ?? data.uninsuredExpenseRule ?? 0,
    });

    const [items, setItems] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);

    const expenseAccountSelect = {
        api: 'api/' + Resources.AccountsQuick,
        query: {MainAccountTypeID: MAJOR_ACCOUNT_TYPE_EXPENSE},
        searchMap: (item) => ({
            label: item.AccountNumber + ' ' + item.AccountName,
            value: item.AccountID
        })
    };

    const incomeAccountSelect = {
        api: 'api/' + Resources.AccountsQuick,
        query: {MainAccountTypeID: MAJOR_ACCOUNT_TYPE_INCOME},
        searchMap: (item) => ({
            label: item.AccountNumber + ' ' + item.AccountName,
            value: item.AccountID
        })
    };

    const selects = {
        glOutAccountID: expenseAccountSelect,
        glInAccountID: incomeAccountSelect,
    };

    // FieldDropdownSelect calls onChange(name, selectedOption) where selectedOption is { value, label }.
    // When loading from API, GLOutAccountID is a raw number. extractId handles both.
    const extractId = (v) => (v && typeof v === 'object') ? v.value : (v ?? null);

    const fetchData = () => {
        dispatch(getResource({
            user: LocalStorage.get('user'),
            query: {},
            resource: Resources.ClaimsActionItems
        }));
    };

    const onSubmit = () => {
        dispatch(updateResource({
            user: LocalStorage.get('user'),
            params: {
                items: items.map(({name, glOutAccountID, glInAccountID, recoverableRule, deductibleRule, lossRunRule, uninsuredExpenseRule}) => ({
                    name,
                    glOutAccountID: extractId(glOutAccountID),
                    glInAccountID: extractId(glInAccountID),
                    recoverableRule,
                    deductibleRule,
                    lossRunRule,
                    uninsuredExpenseRule,
                }))
            },
            resource: Resources.ClaimsActionItems,
            piggyResource: Resources.ClaimsActionItems,
            errorMessage: true,
            successMessage: 'Settings saved successfully!'
        }));
        setIsDirty(false);
    };

    const handleFormCancel = () => {
        const list = getProp(resource, 'data.list', []);
        setItems(list.map(makeItem));
        setIsDirty(false);
    };

    const handleAdd = () => {
        setItems(prev => [...prev, makeItem()]);
        setIsDirty(true);
    };

    const handleChange = (index, fieldName, value) => {
        setItems(prev => prev.map((item, i) => {
            if (i !== index) return item;
            let updated;
            if (value && typeof value === 'object' && 'value' in value) {
                updated = {...item, [fieldName]: value.value, [fieldName.replace('ID', '')]: value.label ?? ''};
            } else {
                updated = {...item, [fieldName]: value};
            }
            if (fieldName === 'glOutAccountID' && value) {
                updated.glInAccountID = null;
                updated.glInAccount = '';
            } else if (fieldName === 'glInAccountID' && value) {
                updated.glOutAccountID = null;
                updated.glOutAccount = '';
            }
            return updated;
        }));
        setIsDirty(true);
    };

    const handleRemove = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
        setIsDirty(true);
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!isLoading) {
            const list = getProp(resource, 'data.list', []);
            setItems(list.map(makeItem));
        }
    }, [isLoading]);

    useEffect(() => {
        setIsTabDirty(isDirty);
    }, [isDirty]);

    return (
        <div className="grid space-y-8 w-full mx-auto py-8 max-w-[1800px] min-w-[900px]">
            <Card bodyClass="py-8 px-6 space-y-4" addClass="mb-4">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-semibold text-tm-gray-900">
                            {translate('title.claim_action_types')}
                        </h2>

                        <p className="mt-1 text-sm text-tm-gray-500">
                            {translate('text.claim_action_types_subtitle')}
                        </p>
                    </div>

                    <Button
                        onClick={handleAdd}
                        className="btn btn-primary shrink-0"
                    >
                        <PlusIcon className="w-4 h-4 mr-1"/>
                        {translate('btn.add_action_type')}
                    </Button>
                </div>

                {isLoading && (
                    <div className="animate-pulse space-y-3">
                        <div className="grid [grid-template-columns:repeat(16,minmax(0,1fr))] gap-4 px-1 pb-1 pr-9">
                            <div className="col-span-2 h-4 bg-tm-gray-200 rounded-btn"/>

                            <div className="col-span-3 h-4 bg-tm-gray-200 rounded-btn"/>

                            <div className="col-span-3 h-4 bg-tm-gray-200 rounded-btn"/>

                            <div className="col-span-2 h-4 bg-tm-gray-200 rounded-btn"/>

                            <div className="col-span-2 h-4 bg-tm-gray-200 rounded-btn"/>

                            <div className="col-span-2 h-4 bg-tm-gray-200 rounded-btn"/>

                            <div className="col-span-2 h-4 bg-tm-gray-200 rounded-btn"/>
                        </div>

                        {Array.from({length: 4}).map((_, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <div className="grid [grid-template-columns:repeat(16,minmax(0,1fr))] gap-4 items-center flex-1">
                                    <div className="col-span-2 h-9 bg-tm-gray-200 rounded-btn"/>

                                    <div className="col-span-3 h-9 bg-tm-gray-200 rounded-btn"/>

                                    <div className="col-span-3 h-9 bg-tm-gray-200 rounded-btn"/>

                                    <div className="col-span-2 flex justify-center">
                                        <div className="h-6 w-full bg-tm-gray-200 rounded-btn"/>
                                    </div>

                                    <div className="col-span-2 flex justify-center">
                                        <div className="h-6 w-full bg-tm-gray-200 rounded-btn"/>
                                    </div>

                                    <div className="col-span-2 flex justify-center">
                                        <div className="h-6 w-full bg-tm-gray-200 rounded-btn"/>
                                    </div>

                                    <div className="col-span-2 flex justify-center">
                                        <div className="h-6 w-full bg-tm-gray-200 rounded-btn"/>
                                    </div>
                                </div>

                                <div className="w-7 shrink-0"/>
                            </div>
                        ))}
                    </div>
                )}

                {!isLoading && (
                    <>
                        {items.length > 0 && (
                            <div className="grid [grid-template-columns:repeat(16,minmax(0,1fr))] gap-4 px-1 pb-1 pr-9">
                                <p className="col-span-2 text-sm font-semibold text-tm-gray-700">{translate('field.Name')}</p>

                                <p className="col-span-3 text-sm font-semibold text-tm-gray-700">{translate('field.GLAccount_MoneyOut')}</p>

                                <p className="col-span-3 text-sm font-semibold text-tm-gray-700">{translate('field.GLAccount_MoneyIn')}</p>

                                <div className="col-span-2 flex items-center justify-center gap-1">
                                    <p className="text-xs font-semibold text-tm-gray-700 text-center">{translate('field.RecoverableRule')}</p>

                                    <Tooltip content={translate('text.rule_recoverable_tooltip')}>
                                        <InformationCircleIcon className="w-3.5 h-3.5 text-tm-gray-500 cursor-default shrink-0"/>
                                    </Tooltip>
                                </div>

                                <div className="col-span-2 flex items-center justify-center gap-1">
                                    <p className="text-xs font-semibold text-tm-gray-700 text-center">{translate('field.DeductibleRule')}</p>

                                    <Tooltip content={translate('text.rule_deductible_tooltip')}>
                                        <InformationCircleIcon className="w-3.5 h-3.5 text-tm-gray-500 cursor-default shrink-0"/>
                                    </Tooltip>
                                </div>

                                <div className="col-span-2 flex items-center justify-center gap-1">
                                    <p className="text-xs font-semibold text-tm-gray-700 text-center">{translate('field.LossRunRule')}</p>

                                    <Tooltip content={translate('text.rule_loss_run_tooltip')}>
                                        <InformationCircleIcon className="w-3.5 h-3.5 text-tm-gray-500 cursor-default shrink-0"/>
                                    </Tooltip>
                                </div>

                                <div className="col-span-2 flex items-center justify-center gap-1">
                                    <p className="text-xs font-semibold text-tm-gray-700 text-center">{translate('field.UninsuredExpenseRule')}</p>

                                    <Tooltip content={translate('text.rule_uninsured_expense_tooltip')}>
                                        <InformationCircleIcon className="w-3.5 h-3.5 text-tm-gray-500 cursor-default shrink-0"/>
                                    </Tooltip>
                                </div>
                            </div>
                        )}

                        <div className="space-y-3">
                            {items.map((item, index) => (
                                <div key={item._uid} className="flex items-center gap-2">
                                    <div className="grid [grid-template-columns:repeat(16,minmax(0,1fr))] gap-4 items-center flex-1">
                                        <FieldsForm
                                            fieldsState={fillFieldsFromData(getItemFields(item), item)}
                                            onChange={(fieldName, value) => handleChange(index, fieldName, value)}
                                            selects={selects}
                                            translate={translate}
                                        />

                                        <div className="col-span-2 flex justify-center">
                                            <RuleSelect
                                                value={item.recoverableRule}
                                                onChange={val => handleChange(index, 'recoverableRule', val)}
                                                translate={translate}
                                            />
                                        </div>

                                        <div className="col-span-2 flex justify-center">
                                            <RuleSelect
                                                value={item.deductibleRule}
                                                onChange={val => handleChange(index, 'deductibleRule', val)}
                                                translate={translate}
                                            />
                                        </div>

                                        <div className="col-span-2 flex justify-center">
                                            <RuleSelect
                                                value={item.lossRunRule}
                                                onChange={val => handleChange(index, 'lossRunRule', val)}
                                                translate={translate}
                                            />
                                        </div>

                                        <div className="col-span-2 flex justify-center">
                                            <RuleSelect
                                                value={item.uninsuredExpenseRule}
                                                onChange={val => handleChange(index, 'uninsuredExpenseRule', val)}
                                                translate={translate}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        className="rounded-btn text-tm-gray-500 hover:text-red-600 transition-colors w-7 shrink-0"
                                        onClick={() => setDeleteIndex(index)}
                                    >
                                        <TrashIcon className="w-5 h-5"/>
                                    </button>
                                </div>
                            ))}
                        </div>

                        {items.length === 0 && (
                            <p className="text-sm text-tm-gray-500 py-4 text-center">
                                {translate('text.no_action_items_yet')}
                            </p>
                        )}
                    </>
                )}

                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 border-t border-tm-gray-100">
                    <span className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide">
                        {translate('text.rule_key')}
                    </span>

                    <span className="flex items-center gap-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-btn text-xs font-medium bg-green-100 text-green-800">
                            {translate('text.rule_always')}
                        </span>

                        <span className="text-xs text-tm-gray-600">{translate('text.rule_always_desc')}</span>
                    </span>

                    <span className="flex items-center gap-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-btn text-xs font-medium bg-tm-gray-100 text-tm-gray-700">
                            {translate('text.rule_never')}
                        </span>

                        <span className="text-xs text-tm-gray-600">{translate('text.rule_never_desc')}</span>
                    </span>

                    <span className="flex items-center gap-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-btn text-xs font-medium bg-amber-100 text-amber-800">
                            {translate('text.rule_ask_on_entry')}
                        </span>

                        <span className="text-xs text-tm-gray-600">{translate('text.rule_ask_on_entry_desc')}</span>
                    </span>
                </div>

                <div className="flex items-start gap-2 bg-tm-gray-50 rounded-lg p-3">
                    <InformationCircleIcon className="w-4 h-4 text-tm-gray-400 shrink-0 mt-0.5"/>

                    <p className="text-xs text-tm-gray-600">
                        {translate('text.ask_on_entry_note')}
                    </p>
                </div>
            </Card>

            <PageFooter
                translate={translate}
                canSubmit={isDirty && checkPerm(Resources.AccountingSettings, UPDATE_PERM)}
                actionCancel={handleFormCancel}
                actionSubmit={onSubmit}
            />

            <ModalConfirm
                title={translate('modal_heading.confirm')}
                show={deleteIndex !== null}
                onClose={() => setDeleteIndex(null)}
                buttonLabel={translate('btn.confirm')}
                closeButtonLabel={translate('btn.cancel')}
                translate={translate}
                onConfirm={() => {
                    handleRemove(deleteIndex);
                    setDeleteIndex(null);
                }}
            >
                {translate('text.confirm_delete_action_type')}
            </ModalConfirm>
        </div>
    );
};

export default ClaimsActionItemsTab;
